import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../amazon/Amazon.css';

const API = '/api/foxtrot';

const NS_SO_LINK = (id) =>
  id ? `https://3646798.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${id}` : null;

const TABS = [
  { key: 'pending_review', label: 'Pending Review' },
  { key: 'hold', label: 'Hold' },
  { key: 'booked', label: 'Booked' },
  { key: 'all', label: 'All' },
];

function Badge({ status }) {
  const map = {
    pending_review: 'pending',
    hold: 'warning',
    approved: 'approved',
    booked: 'ok',
    rejected: 'critical',
    error: 'critical',
  };
  return (
    <span className={`amazon-badge ${map[status] || 'pending'}`}>
      {(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

const fmtMoney = (n) =>
  n == null ? '--' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function orderTotal(lines) {
  if (!Array.isArray(lines)) return null;
  let total = 0;
  for (const l of lines) {
    if (l.rate == null || !l.qty) return null;
    total += Number(l.rate) * l.qty;
  }
  return total;
}

export default function FoxtrotOrders() {
  const [tab, setTab] = useState('pending_review');
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null); // full row for the expanded order
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // order id being approved/rejected
  const [message, setMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [holdDays, setHoldDays] = useState(2);
  const [me, setMe] = useState({ actor: null, isApprover: false, approvers: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === 'all' ? {} : { status: tab };
      const { data } = await axios.get(`${API}/queue`, { params });
      setOrders(data.orders || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Whether the signed-in user may release a customer notice. The server enforces
  // this on every approve call; this only decides which buttons to render.
  useEffect(() => {
    axios.get(`${API}/me`)
      .then(({ data }) => setMe(data))
      .catch(() => {});
  }, []);

  const openDetail = async (id) => {
    if (detail?.id === id) { setDetail(null); return; }
    try {
      const { data } = await axios.get(`${API}/queue/${id}`);
      setDetail(data);
      setReplyText(data.backorder_reply_draft || '');
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    }
  };

  // Deep link from the CS-confirmation email: /foxtrot?order=<id>
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('order');
    if (id) { setTab('all'); openDetail(id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitNotice = async (id) => {
    if (!window.confirm('Submit this notice for approval? Nothing is sent to Foxtrot yet.')) return;
    setBusy(id);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/queue/${id}/backorder/submit`, { body: replyText });
      setMessage({ type: 'info', text: `Submitted — ${(data.approvers || []).join(' or ')} must approve before it reaches Foxtrot.` });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const approveNotice = async (id) => {
    if (!window.confirm('Approve and send this notice to Foxtrot now? This emails the customer.')) return;
    setBusy(id);
    setMessage(null);
    try {
      await axios.post(`${API}/queue/${id}/backorder/approve`);
      setMessage({ type: 'info', text: 'Approved — notice sent to Foxtrot.' });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const rejectNotice = async (id) => {
    const reason = window.prompt('Send back to CS — what needs changing?');
    if (reason === null) return;
    setBusy(id);
    setMessage(null);
    try {
      await axios.post(`${API}/queue/${id}/backorder/reject`, { reason });
      setMessage({ type: 'info', text: 'Sent back to CS. Nothing was sent to Foxtrot.' });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const holdOrder = async (id) => {
    setBusy(id);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/queue/${id}/backorder/hold`, { days: holdDays });
      setMessage({ type: 'info', text: `Holding until ${data.until} — stock will be rechecked automatically.` });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const dismissBackorder = async (id) => {
    const note = window.prompt('Optional note (e.g. "Foxtrot cancelled remaining items"):');
    if (note === null) return;
    setBusy(id);
    setMessage(null);
    try {
      await axios.post(`${API}/queue/${id}/backorder/dismiss`, { note });
      setMessage({ type: 'info', text: 'Backorder closed — no notice sent, sales order left as-is for you to manage.' });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const pollNow = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/poll`);
      setMessage({ type: 'info', text: `Scanned ${data.scanned}, ingested ${data.ingested}${data.errors?.length ? `, errors: ${data.errors.join('; ')}` : ''}` });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id) => {
    if (!window.confirm('Book this order as a NetSuite sales order?')) return;
    setBusy(id);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/queue/${id}/approve`, { approved_by: 'run-app' });
      setMessage({ type: 'info', text: `Booked SO ${data.tranId || data.nsSoId}` });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejecting this order?');
    if (reason === null) return;
    setBusy(id);
    try {
      await axios.post(`${API}/queue/${id}/reject`, { rejected_by: 'run-app', reason });
      setDetail(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="amazon-page">
      <div className="amazon-page-header">
        <h1>Foxtrot Orders</h1>
        <p style={{ color: '#666', marginTop: 4 }}>
          Order emails from Foxtrot land here automatically. Approving books the NetSuite
          sales order with the PO number and store ship-to. Invoices email to their Ramp
          inbox on their own.
        </p>
      </div>

      <div className="amazon-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`amazon-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => { setTab(t.key); setDetail(null); }}
          >
            {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <button className="amazon-btn" onClick={pollNow} disabled={loading}>
            {loading ? 'Working…' : 'Check email now'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 8,
          background: message.type === 'error' ? '#fde8e8' : '#e8f5e9',
          color: message.type === 'error' ? '#C0392B' : '#27AE60',
        }}>
          {message.text}
        </div>
      )}

      <table className="amazon-table">
        <thead>
          <tr>
            <th>Received</th>
            <th>Store</th>
            <th>PO #</th>
            <th>Deliver by</th>
            <th>Status</th>
            <th>Flags</th>
            <th>NS SO</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
              {loading ? 'Loading…' : 'No orders'}
            </td></tr>
          )}
          {orders.map((o) => (
            <React.Fragment key={o.id}>
              <tr onClick={() => openDetail(o.id)} style={{ cursor: 'pointer' }}>
                <td>{o.received_at ? new Date(o.received_at).toLocaleDateString() : '--'}</td>
                <td>{o.store || '--'}</td>
                <td>{o.po_number || '--'}</td>
                <td>{o.deliver_by || '--'}</td>
                <td><Badge status={o.status} /></td>
                <td>
                  {o.backorder_status === 'pending_cs' && <span className="amazon-badge critical">backorder — needs CS</span>}
                  {o.backorder_status === 'pending_approval' && <span className="amazon-badge critical">notice awaiting approval</span>}
                  {o.backorder_status === 'holding' && <span className="amazon-badge warning">holding until {o.backorder_hold_until}</span>}
                  {o.backorder_status === 'notice_sent' && <span className="amazon-badge ok">customer notified</span>}
                  {o.backorder_status === 'resolved' && <span className="amazon-badge ok">restocked</span>}
                  {o.backorder_status === 'cs_handled' && <span className="amazon-badge ok">handled by CS</span>}
                  {o.backorder_flag && !o.backorder_status && <span className="amazon-badge warning">short stock</span>}
                  {Array.isArray(o.hold_reasons) && o.hold_reasons.length > 0 && (
                    <span className="amazon-badge critical" title={o.hold_reasons.join('\n')}>
                      {o.hold_reasons.length} issue{o.hold_reasons.length > 1 ? 's' : ''}
                    </span>
                  )}
                </td>
                <td>
                  {o.ns_so_id ? (
                    <a href={NS_SO_LINK(o.ns_so_id)} target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       style={{ color: '#8B6F4E', fontWeight: 500 }}>
                      {o.ns_so_tranid || o.ns_so_id}
                    </a>
                  ) : '--'}
                </td>
                <td>
                  {o.status === 'pending_review' && (
                    <>
                      <button className="amazon-btn primary" disabled={busy === o.id}
                              onClick={(e) => { e.stopPropagation(); approve(o.id); }}>
                        {busy === o.id ? 'Booking…' : 'Approve'}
                      </button>{' '}
                      <button className="amazon-btn" disabled={busy === o.id}
                              onClick={(e) => { e.stopPropagation(); reject(o.id); }}>
                        Reject
                      </button>
                    </>
                  )}
                  {o.status === 'hold' && (
                    <button className="amazon-btn" disabled={busy === o.id}
                            onClick={(e) => { e.stopPropagation(); reject(o.id); }}>
                      Reject
                    </button>
                  )}
                </td>
              </tr>
              {detail?.id === o.id && (
                <tr>
                  <td colSpan={8} style={{ background: '#FAFAF8', padding: 16 }}>
                    {/* Foxtrot changed their email format once already — when the
                        template parser fails, AI reads the order instead and it
                        lands here for a human to check against the original. */}
                    {detail.parsed?.aiParsed && (
                      <div style={{ marginBottom: 10, padding: 10, background: '#fff8e6',
                                    border: '1px solid #e0c98b', borderRadius: 6 }}>
                        <strong>Read by AI, not the template parser.</strong> Foxtrot's email didn't match
                        the expected format, so the lines below were extracted by {detail.parsed.aiModel || 'AI'}.
                        Check the quantities, PO, and address against the original email before approving.
                        {detail.gmail_thread_id && (
                          <> <a href={`https://mail.google.com/mail/u/0/#all/${detail.gmail_thread_id}`}
                                target="_blank" rel="noreferrer">Open the email</a>.</>
                        )}
                      </div>
                    )}
                    <div style={{ marginBottom: 8 }}>
                      <strong>Ship to:</strong> {detail.ship_address || '--'}
                      {detail.ship_entry_id ? ` (address book entry ${detail.ship_entry_id})` : ' — UNRESOLVED'}
                    </div>
                    {Array.isArray(detail.hold_reasons) && detail.hold_reasons.length > 0 && (
                      <div style={{ marginBottom: 8, color: '#a33' }}>
                        <strong>Issues:</strong>
                        <ul style={{ margin: '4px 0 0 20px' }}>
                          {detail.hold_reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    <table className="amazon-table" style={{ margin: 0 }}>
                      <thead>
                        <tr><th>Cases</th><th>Product (email)</th><th>SKU</th><th>Rate</th><th>Available</th></tr>
                      </thead>
                      <tbody>
                        {(detail.resolved_lines || []).map((l, i) => (
                          <tr key={i} style={l.short ? { color: '#a33' } : undefined}>
                            <td>{l.qty}</td>
                            <td>{l.productName}</td>
                            <td>{l.sku || 'UNRESOLVED'}</td>
                            <td>{fmtMoney(l.rate)}</td>
                            <td>{l.available == null ? '--' : `${l.available}${l.short ? ' (SHORT)' : ''}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 8, fontWeight: 600 }}>
                      Order total (before shipping): {fmtMoney(orderTotal(detail.resolved_lines))}
                    </div>

                    {detail.backorder_flag && detail.backorder_status !== 'resolved' && (
                      <div style={{ marginTop: 16, padding: 14, background: '#fff', border: '1px solid #e0d5c5', borderRadius: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          Backorder response
                          {detail.backorder_status === 'notice_sent' && ` — sent ${detail.backorder_notice_sent_at ? new Date(detail.backorder_notice_sent_at).toLocaleString() : ''}${detail.backorder_approved_by ? `, approved by ${detail.backorder_approved_by}` : ''}`}
                          {detail.backorder_status === 'holding' && ` — holding until ${detail.backorder_hold_until}`}
                          {detail.backorder_status === 'pending_approval' && ' — awaiting approval'}
                        </div>

                        {/* Awaiting sign-off: the text is frozen. Only an approver can release it. */}
                        {detail.backorder_status === 'pending_approval' && (
                          <>
                            <p style={{ color: '#666', margin: '4px 0 8px' }}>
                              Submitted by {detail.backorder_submitted_by || 'CS'}
                              {detail.backorder_submitted_at && ` on ${new Date(detail.backorder_submitted_at).toLocaleString()}`}.
                              {' '}Nothing has been sent to Foxtrot. {me.isApprover
                                ? 'Approving sends exactly this text to the customer.'
                                : `Waiting on ${(me.approvers || []).join(' or ')} to approve.`}
                            </p>
                            <pre style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 8,
                                          border: '1px solid #ddd', borderRadius: 6, background: '#FAFAF8',
                                          whiteSpace: 'pre-wrap', margin: 0 }}>
                              {detail.backorder_reply_draft}
                            </pre>
                            {me.isApprover && (
                              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button className="amazon-btn primary" disabled={busy === detail.id}
                                        onClick={() => approveNotice(detail.id)}>
                                  {busy === detail.id ? 'Sending…' : 'Approve & send to Foxtrot'}
                                </button>
                                <button className="amazon-btn" disabled={busy === detail.id}
                                        onClick={() => rejectNotice(detail.id)}>
                                  Send back to CS
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* CS stage: draft, then submit for approval. */}
                        {detail.backorder_status !== 'notice_sent' && detail.backorder_status !== 'pending_approval' && (
                          <>
                            <p style={{ color: '#666', margin: '4px 0 8px' }}>
                              If restock is close, hold the order instead of notifying Foxtrot — stock is rechecked
                              automatically when the hold expires. Submitting does not contact Foxtrot; only
                              {' '}{(me.approvers || ['the approver']).join(' or ')} can release the notice.
                            </p>
                            <textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              rows={10}
                              style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
                            />
                            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button className="amazon-btn primary" disabled={busy === detail.id}
                                      onClick={() => submitNotice(detail.id)}>
                                {busy === detail.id ? 'Submitting…' : 'Submit for approval'}
                              </button>
                              <span style={{ color: '#999' }}>or</span>
                              <button className="amazon-btn" disabled={busy === detail.id}
                                      onClick={() => holdOrder(detail.id)}>
                                Hold for restock
                              </button>
                              <select value={holdDays} onChange={(e) => setHoldDays(Number(e.target.value))}
                                      style={{ padding: 6, border: '1px solid #ddd', borderRadius: 6 }}>
                                <option value={1}>1 day</option>
                                <option value={2}>2 days</option>
                                <option value={3}>3 days</option>
                                <option value={5}>5 days</option>
                                <option value={7}>7 days</option>
                              </select>
                              <span style={{ color: '#999' }}>or</span>
                              <button className="amazon-btn" disabled={busy === detail.id}
                                      onClick={() => dismissBackorder(detail.id)}
                                      title="No notice, no reminders — the sales order stays as-is for you to manage (e.g. Foxtrot cancelled the remaining items)">
                                I'm handling it — close
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
